import React, { useState } from "react";
import { PageShell, SoftCard } from "./ui";
import DailyInsights from "./DailyInsights";
import { useAstriaCopy, CopyKey } from "./copy";
import { useAppLanguage } from "../../utils/appLanguage";
import { useZodiacToday } from "./useZodiacToday";

type SectionId = "energy" | "other";

const TRADITIONS = ["Western", "Vedic", "Chinese", "Saju"];

const Accordion: React.FC<{
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, open, onToggle, children }) => (
  <SoftCard premium={open}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center justify-between text-left"
    >
      <span>
        <span className="block text-lg font-light text-astria-text">{title}</span>
        {subtitle && <span className="mt-1 block text-sm text-astria-muted">{subtitle}</span>}
      </span>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--astria-accent-primary)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
    <div
      className="grid transition-all duration-[220ms] ease-out"
      style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }}
      aria-hidden={!open}
    >
      <div className="overflow-hidden">
        <div className="pt-4">{children}</div>
      </div>
    </div>
  </SoftCard>
);

const AstrologyPage: React.FC = () => {
  const c = useAstriaCopy();
  // Everything starts collapsed; opening one closes the others.
  const [open, setOpen] = useState<SectionId | null>(null);
  const toggle = (id: SectionId) => setOpen((cur) => (cur === id ? null : id));
  const t = (k: CopyKey) => c(k);
  const reading = useZodiacToday(useAppLanguage());

  return (
    <PageShell title={t("navAstrology")} sentence={t("astroSub")}>
      <DailyInsights reading={reading} />

      <Accordion title={t("astroEnergyTitle")} open={open === "energy"} onToggle={() => toggle("energy")}>
        {reading ? (
          <>
            <p className="text-lg font-light text-astria-text">{reading.theme}</p>
            <p className="mt-3 text-astria-text">{reading.message}</p>
            <p className="mt-3 text-astria-muted">{reading.lifeGPSSignal}</p>
          </>
        ) : (
          <p className="text-astria-muted">…</p>
        )}
      </Accordion>

      <Accordion
        title={t("astroOtherTitle")}
        subtitle={t("astroOtherSub")}
        open={open === "other"}
        onToggle={() => toggle("other")}
      >
        <div className="flex flex-wrap gap-2">
          {TRADITIONS.map((name) => (
            <span
              key={name}
              className="px-4 py-2 text-sm text-astria-text"
              style={{ borderRadius: "var(--astria-radius-pill)", background: "var(--astria-surface-soft)" }}
            >
              {name}
            </span>
          ))}
        </div>
      </Accordion>

      <p className="text-center text-xs text-astria-muted">{t("astroNote")}</p>
    </PageShell>
  );
};

export default AstrologyPage;
